"use client"

import { useCallback } from "react"

import type { WooCommerceProduct } from "@/services/woo-commerce-service"

import { SEARCH_MIN_LENGTH } from "../constants"

interface UseSearchKeyboardNavigationProps {
  open: boolean
  setOpen: (open: boolean) => void
  searchValue: string
  productsList: WooCommerceProduct[]
  inputRef: React.RefObject<HTMLInputElement | null>
  performSearch: (query: string) => void
}

interface UseSearchKeyboardNavigationReturn {
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleCommandKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
}

export function useSearchKeyboardNavigation({
  open,
  setOpen,
  searchValue,
  productsList,
  inputRef,
  performSearch,
}: UseSearchKeyboardNavigationProps): UseSearchKeyboardNavigationReturn {
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Escape: close popover and keep focus on input
      if (e.key === "Escape" && open) {
        e.preventDefault()
        setOpen(false)
        return
      }

      // Enter: perform search
      if (e.key === "Enter") {
        e.preventDefault()
        if (searchValue.length >= SEARCH_MIN_LENGTH) {
          performSearch(searchValue)
        }
        return
      }

      // ArrowDown: let Command handle selection naturally
      // We only need to ensure the popover is open with results
      if (e.key === "ArrowDown" && open && productsList.length > 0) {
        // Prevent default cursor movement in input
        e.preventDefault()

        // Let Command component handle the selection via its default behavior
        // It will automatically select the first item when arrow keys are pressed
        const commandList = document.querySelector("[cmdk-list]") as HTMLElement
        if (commandList) {
          commandList.focus()
        }
      }
    },
    [open, setOpen, productsList, searchValue, performSearch]
  )

  const handleCommandKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Escape: return focus to input and close popover
      if (e.key === "Escape") {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(false)
        return
      }
    },
    [inputRef, setOpen]
  )

  return {
    handleInputKeyDown,
    handleCommandKeyDown,
  }
}
